<script>
	import { onMount } from 'svelte';
	import { fetchTopList, displayInt } from '../utils/utils.js';
	import { Circle2 } from 'svelte-loading-spinners';
    import { Link } from 'svelte-routing';

	let topBrf = null;
	let loading = true;
	let error = '';

	function slugify(text) {
		if (!text) return '';
		return text.toString().toLowerCase()
			.replace(/å/g, 'a').replace(/ä/g, 'a').replace(/ö/g, 'o')
			.replace(/[^\w\-]+/g, '-').replace(/\-\-+/g, '-')
			.replace(/^-+/, '').replace(/-+$/, '');
	}

	function createBrfLink(id, name) {
		const slug = slugify(name);
		return `/brf/${id}/${slug}`;
	}

	onMount(async () => {
		try {
			const allBrfs = await fetchTopList();
			if (allBrfs && allBrfs.length > 0) {
				const sortedByRating = [...allBrfs].sort((a, b) => b.rating - a.rating);
				topBrf = sortedByRating[0];
			} else {
				error = 'Kunde inte hitta några föreningar.';
			}
		} catch (err) {
			console.error('Error fetching top BRF:', err);
			error = 'Kunde inte ladda topplistan.';
		} finally {
			loading = false;
		}
	});

	function getRatingColor(rating) {
		if (rating >= 80) return '#28a745'; // Green
		if (rating >= 60) return '#ffc107'; // Yellow
		if (rating >= 40) return '#fd7e14'; // Orange
		return '#dc3545'; // Red
	}
</script>

<div class="top-brf-container">
	{#if loading}
		<div class="loader-container">
			<Circle2 size="50" color="#FF3E00" />
		</div>
	{:else if error}
		<p class="error-message">{error}</p>
	{:else if topBrf}
		<div class="header">
			<i class="fas fa-trophy trophy-icon"></i>
			<h2>Sveriges högst rankade förening</h2>
		</div>
		<a href={createBrfLink(topBrf.id, topBrf.name)} class="brf-card-link" target="_blank" rel="noopener noreferrer">
			<div class="brf-card">
				<div class="card-header">
					<h3 class="brf-name"><i class="fa-solid fa-building fa-sm icon-detail"></i>{topBrf.name}</h3>
					<div class="rating-badge" style="background-color: {getRatingColor(topBrf.rating)}">
						<span class="rating-value">{topBrf.rating}</span>
					</div>
				</div>
				<div class="key-metrics">
					<div class="metric">
						<span class="metric-label">Lägenheter</span>
						<span class="metric-value">{topBrf.numberOfApartments} st</span>
					</div>
					<div class="metric">
						<span class="metric-label">Avgift/kvm</span>
						<span class="metric-value">{topBrf.feePerSqm ? displayInt(topBrf.feePerSqm) : '–'} kr</span>
					</div>
					{#if topBrf.foundingYear}
						<div class="metric">
							<span class="metric-label">Byggår</span>
							<span class="metric-value">{topBrf.foundingYear}</span>
						</div>
					{/if}
				</div>
			</div>
		</a>
		<div class="cta-container">
            <Link to="/toplist">
			    <button class="action-button">Se hela topplistan <span class="button-arrow">→</span></button>
            </Link>
		</div>
	{/if}
</div>

<style>
	.top-brf-container {
		background-color: #f8f9fa;
		border-radius: 16px;
		padding: 30px;
		max-width: 500px;
		margin: 0 auto;
		box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        border: 1px solid #e9ecef;
        box-sizing: border-box;
	}

	.header {
		text-align: center;
		margin-bottom: 25px;
	}
	
	.trophy-icon {
		color: var(--hover);
		font-size: 2.5rem;
		margin-bottom: 10px;
	}

	.header h2 {
		font-size: 1.6rem;
		color: var(--box-title-color);
		margin: 0;
	}

	.brf-card-link {
		text-decoration: none;
		color: inherit;
		display: block;
	}

	.brf-card {
		background-color: #ffffff;
		border-radius: 12px;
		padding: 20px;
		transition: transform 0.2s ease, box-shadow 0.2s ease;
		border: 1px solid #dee2e6;
	}
	
	.brf-card:hover {
		transform: translateY(-5px);
		box-shadow: 0 8px 20px rgba(0,0,0,0.12);
	}

	.card-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 20px;
		gap: 15px;
	}

	.brf-name {
		font-size: 1.25rem;
		font-weight: 600;
		margin: 0;
		line-height: 1.3;
	}

	.icon-detail {
		color: var(--hover);
		margin-right: 6px;
	}

	.rating-badge {
		flex-shrink: 0;
		width: 55px;
		height: 55px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: white;
		box-shadow: 0 2px 5px rgba(0,0,0,0.15);
	}
	
	.rating-value {
		font-family: 'MuseoModerna', sans-serif;
		font-size: 1.5rem;
		font-weight: 700;
	}

	.key-metrics {
		display: flex;
		justify-content: space-around;
		gap: 15px;
		padding-top: 15px;
		border-top: 1px solid #e9ecef;
	}

	.metric {
		text-align: center;
	}

	.metric-label {
		display: block;
		font-size: 0.8rem;
		color: #6c757d;
		text-transform: uppercase;
		margin-bottom: 5px;
	}

	.metric-value {
		font-size: 1rem;
		font-weight: 600;
	}
	
	.cta-container {
		text-align: center;
		margin-top: 25px;
	}
	
	.loader-container {
		display: flex;
		justify-content: center;
		align-items: center;
		min-height: 200px;
	}

    @media (max-width: 550px) {
        .top-brf-container {
            padding: 20px 15px;
        }
    }
</style>
